import { Card, CardContent } from "@/components/ui/card";

import { Feedback } from "@/lib/messaging";
import { LucideIcon } from "lucide-react";
import { MetricColorNumber } from "@/components/common/metrics-color-number";
import { ResetModeButton } from "@/components/common/reset-mode-button";
import { TooltipWithIcon } from "@/components/tooltip-with-icon";
import { cn } from "@/lib/utils";
import { getMetricIcon } from "@/lib/metrics";
import { useTranslation } from "react-i18next";

interface FeedbackAnalysisProps {
  feedbackData: Feedback;
  restartMode?: () => void;
  size?: "xs" | "sm" | "lg";
  continuationType?: "continue" | "end" | undefined;
  inline?: boolean;
}

export function FeedbackAnalysisCard({
  feedbackData,
  restartMode = () => {},
  size = "lg",
  continuationType,
  inline = false,
}: FeedbackAnalysisProps) {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <Card
      className={cn(
        "bg-secondary text-secondary-foreground",
        inline ? "max-h-80 overflow-auto" : "",
      )}
    >
      <CardContent>
        <div className={size == "lg" ? "p-4" : "p-2"}>
          {feedbackData.analysis && (
            <div className="pb-4 bg-muted/50 rounded-md text-sm">
              <p>{feedbackData.analysis}</p>
            </div>
          )}
          {size === "xs" ? (
            <div className="flex flex-row space-x-4">
              {Object.entries(feedbackData.metrics).map(
                ([metric, value]) =>
                  value >= 0 && (
                    <div
                      key={metric}
                      className="flex flex-col items-center space-x-2"
                    >
                      <TooltipWithIcon
                        className="top-2 right-0 text-ternary pb-2"
                        content={t(`pages:dashboard.metrics.${metric}`)}
                        icon={getMetricIcon(metric) as LucideIcon}
                        iconProps={{ size: 18 }}
                        side="left"
                      />
                      <MetricColorNumber value={value} size="xs" />
                    </div>
                  ),
              )}
            </div>
          ) : (
            <div className="space-y-1 grid grid-cols-2 lg:grid-cols-3">
              {Object.entries(feedbackData.metrics).map(
                ([metric, value]) =>
                  value >= 0 && (
                    <div
                      key={metric}
                      className="flex items-center justify-start space-x-2"
                    >
                      <MetricColorNumber
                        value={value}
                        size={size === "sm" ? "xs" : "sm"}
                      />
                      <span className="text-sm font-normal pr-2">
                        {t(`pages:dashboard.metrics.${metric}`)}
                      </span>
                    </div>
                  ),
              )}
            </div>
          )}
        </div>
        {continuationType && (
          <div className="flex flex-row justify-between rounded-b-xl mt-4 bg-cyan-50 text-black dark:bg-cyan-900 dark:text-gray-200">
            <p className="p-4 pr-12">
              {t(`common:feedback.${continuationType}`)}
            </p>
            <ResetModeButton
              className="top-2 -right-2"
              restartMode={restartMode}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
